import React, { useMemo, useState } from 'react';
import { Layout, Segment, Table, Bubble } from 'tea-component';
import { getLocale, useHistory, useIntl } from 'umi';
import moment from 'moment';
import CardCommon from '@/pages/opinion/components/CardCommon';
import SvgIcon from '@home/components/SvgIcon';
import { getStatsDetailParams } from '@op/utils/storageUtils';
import { getEventName } from '@op/pages/Overview/KeyMetrics/utils';
import { openWindowUrl } from '@op/utils/url';
import { transRegionText } from '@op/utils';
import { NO_DATE } from '@op/constants';
import { getOpinionLineChartEventTypeStyle } from '@intelligence/v3/components/OpinionBarAndLineChart/utils/eventTypeStyle';
import { getOpinionEventTypeDisplayName } from '@intelligence/v3/components/OpinionBarAndLineChart/hooks/utils/getOpinionEventCategoryLabel';
import './index.scss';

const { Body, Content } = Layout;
const { scrollable } = Table.addons;

function formatEmptyCell(value?: string | null): string {
  if (!value || value === NO_DATE.EMPTY) {
    return NO_DATE.DEFAULT;
  }
  return value;
}

function renderDateCell(value?: string | null) {
  const displayText =
    !value || value === NO_DATE.EMPTY
      ? NO_DATE.DEFAULT
      : moment(value).format('YYYY-MM-DD');

  if (displayText === NO_DATE.DEFAULT) {
    return displayText;
  }

  return <div style={{ whiteSpace: 'nowrap' }}>{displayText}</div>;
}

function formatEventRegionText(record: Record<string, any>): string {
  const countryCodes: string[] = Array.isArray(record.countries)
    ? record.countries.filter(Boolean)
    : record.country
      ? [record.country]
      : [];

  if (!countryCodes.length) {
    return NO_DATE.DEFAULT;
  }

  return countryCodes
    .map(code => transRegionText(String(code), 'Global'))
    .join(', ');
}

function renderTextCell(text: string) {
  const displayText = formatEmptyCell(text);
  if (displayText === NO_DATE.DEFAULT) {
    return displayText;
  }

  return (
    <Bubble
      closeOnScroll
      dark
      content={
        <div className="dbo-scroll-bubble-scroll">{displayText}</div>
      }
      overlayClassName="dbo-scroll-bubble"
    >
      <div className="dbi-opinion-event-details__description">{displayText}</div>
    </Bubble>
  );
}

interface IEventDetailsTab {
  name: string;
  value: string;
  name_cn?: string;
  tableRecord: Array<Record<string, any>>;
}

export default function OpinionEventDetails() {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const lang = getLocale();
  const [value, setValue] = useState('all');

  const { time, tabs } = useMemo(() => {
    const data = getStatsDetailParams();
    const { time: detailTime, list } = data || {};
    const allRecord: Array<Record<string, any>> = [];
    const nextTabs: IEventDetailsTab[] = (list || []).map(
      (item: {
        name: string;
        name_cn?: string;
        elements: Array<Record<string, any>>;
      }) => {
        const records = item.elements || [];
        allRecord.push(...records);
        return {
          name: item.name,
          value: item.name,
          name_cn: item.name_cn,
          tableRecord: records,
        };
      },
    );

    nextTabs.unshift({
      name: 'All',
      value: 'all',
      name_cn: '全部',
      tableRecord: allRecord,
    });

    return { time: detailTime, tabs: nextTabs };
  }, []);

  const tableRecord = useMemo(() => {
    const item = tabs.find(tab => tab.value === value);
    return item?.tableRecord || [];
  }, [value, tabs]);

  const tableColumns = useMemo(() => {
    const tableColumnsList: Array<any> = [
      {
        key: 'start_date',
        header: formatMessage({ id: 'dbi_top_event_start_date' }),
        width: 120,
        render: (cvm: Record<string, any>) => renderDateCell(cvm.start_time),
      },
      {
        key: 'end_date',
        header: formatMessage({ id: 'dbi_top_event_end_date' }),
        width: 120,
        render: (cvm: Record<string, any>) => renderDateCell(cvm.end_time),
      },
      {
        key: 'title',
        header: formatMessage({ id: 'Title' }),
        render: (cvm: Record<string, any>) =>
          renderTextCell(getEventName(cvm, lang)),
      },
      {
        key: 'region',
        header: formatMessage({ id: 'Region' }),
        width: 120,
        render: (cvm: Record<string, any>) =>
          renderTextCell(formatEventRegionText(cvm)),
      },
      {
        key: 'description',
        header: formatMessage({ id: 'Description' }),
        render: (cvm: Record<string, any>) =>
          renderTextCell(
            getEventName(
              { name: cvm.description, name_cn: cvm.description_cn },
              lang,
            ),
          ),
      },
      {
        key: 'link',
        header: formatMessage({ id: 'Link' }),
        width: 160,
        render: (cvm: Record<string, any>) => {
          if (!cvm.url) {
            return NO_DATE.DEFAULT;
          }
          return (
            <div
              className="dbi-opinion-event-details__view-link"
              onClick={(event) => {
                event?.stopPropagation();
                event?.preventDefault();
                openWindowUrl(cvm.url);
              }}
            >
              <span>{formatMessage({ id: 'View Link' })}</span>
              <SvgIcon type="other-share-icon" className="dbi-table-icon-link" />
            </div>
          );
        },
      },
    ];

    if (value === 'all') {
      tableColumnsList.unshift({
        key: 'type',
        header: formatMessage({ id: 'Event type' }),
        width: 180,
        render: (cvm: Record<string, any>) => {
          const { textColor, backgroundColor } =
            getOpinionLineChartEventTypeStyle(cvm.event_type);

          return (
            <span
              className="dbi-opinion-event-details__type-tag"
              style={{ color: textColor, backgroundColor }}
            >
              {getOpinionEventTypeDisplayName(
                cvm.event_type,
                lang,
                cvm.event_type_cn,
              )}
            </span>
          );
        },
      });
    }

    return tableColumnsList;
  }, [value, formatMessage, lang]);

  return (
    <Layout className="dbi-opinion-event-details">
      <Body>
        <Content>
          <Content.Header
            showBackButton
            onBackButtonClick={() => history.go(-1)}
            title={formatMessage({ id: 'Event Details' })}
            subtitle={
              <div className="dbi-opinion-event-details__subtitle">{time}</div>
            }
          />
          <Content.Body full>
            <Segment
              className="segment-light dbi-opinion-event-details__segment"
              value={value}
              onChange={segmentValue => setValue(segmentValue)}
              options={tabs.map(item => ({
                text:
                  item.value === 'all'
                    ? getEventName(item, lang)
                    : getOpinionEventTypeDisplayName(
                        item.name,
                        lang,
                        item.name_cn,
                      ),
                value: item.value,
              }))}
            />
            <CardCommon
              className="dbi-opinion-event-details__table-card"
              empty={tableRecord.length === 0}
              emptyHeight={650}
            >
              <Table
                records={tableRecord}
                columns={tableColumns}
                addons={[
                  scrollable({
                    minWidth: value === 'all' ? 1260 : 1080,
                  }),
                ]}
              />
              <div className="dbi-opinion-event-details__total">
                {formatMessage(
                  { id: 'dbi_pagination_total' },
                  { total: tableRecord.length },
                )}
              </div>
            </CardCommon>
          </Content.Body>
        </Content>
      </Body>
    </Layout>
  );
}
